<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="viewer-container">
    <div class="viewer-content">
      <PrimeToolBar class="sticky">
        <template #center>
          <h3 v-if="contentNode && showTitle" class="consultation-title">
            {{ contentNode.name }}
          </h3>
          <PrimeTag
            v-if="$route.params.versionId && showTitle"
            class="tag-version"
            :value="$route.params.versionId"
          />
        </template>
        <template #end>
          <div v-if="showButtons" class="toolbar-actions">
            <PrimeButton
              v-tooltip.bottom="$t('pdf.dezoom')"
              :aria-label="$t('pdf.dezoom')"
              icon="ri-zoom-out-line"
              class="toolbar-btn"
              raised
              @click="zoomOut()"
            />
            <PrimeButton
              v-tooltip.bottom="$t('pdf.zoom')"
              :aria-label="$t('pdf.zoom')"
              icon="ri-zoom-in-line"
              class="toolbar-btn"
              raised
              @click="zoomIn()"
            />
            <PrimeButton
              v-tooltip.bottom="$t('pdf.adaptToWidth')"
              :aria-label="$t('pdf.adaptToWidth')"
              icon="ri-expand-horizontal-line"
              class="toolbar-btn"
              raised
              @click="fitAuto()"
            />
            <PrimeButton
              v-tooltip.bottom="$t(`download`)"
              :aria-label="$t('download')"
              icon="ri-download-line"
              class="toolbar-btn"
              severity="warning"
              raised
              @click="download"
            />
          </div>
        </template>
      </PrimeToolBar>
      <div class="image-container">
        <PrimeImage
          v-if="contentNode && hasContentUrl"
          class="image-rendition"
          :src="contentUrl"
          :alt="contentNode.name"
          :style="zoomStyle"
        />
      </div>
    </div>
  </div>
</template>
<script>
import alfrescoFileService from "@/services/AlfrescoFileService.js";
import Button from "primevue/button";
import Image from "primevue/image";
import ToolBar from "primevue/toolbar";
import Tooltip from "primevue/tooltip";
import Tag from "primevue/tag";

export default {
  name: "ImageViewer",
  components: {
    PrimeButton: Button,
    PrimeImage: Image,
    PrimeToolBar: ToolBar,
    PrimeTag: Tag,
  },
  directives: {
    tooltip: Tooltip,
  },
  props: {
    canUpload: {
      type: Boolean,
      default: false,
    },
    contentNode: {
      type: Object,
      required: true,
    },
    versionId: {
      type: String,
      default: "",
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    showButtons: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["reload"],
  data() {
    return {
      contentUrl: "",
      zoomLevel: 1,
      maxZoomLevel: 2,
      minZoomLevel: 0.5,
    };
  },
  computed: {
    zoomStyle() {
      return {
        transform: `scale(${this.zoomLevel})`,
        transformOrigin: "top center",
        transition: "transform 0.2s ease-in-out",
      };
    },
    hasContentUrl() {
      return this.contentUrl !== "";
    },
    isVersion() {
      return (
        this.$route.params.versionId !== null &&
        this.$route.params.versionId !== undefined
      );
    },
  },
  created() {
    this.loadImage();
    this.fitAuto();
  },
  beforeUpdate() {
    this.loadImage();
  },
  methods: {
    reload() {
      this.$emit("reload");
    },
    download() {
      const link = document.createElement("a");
      link.href = this.contentUrl;
      const fileName = this.contentNode.name;
      let version = "";
      if (this.$route.params.versionId) {
        version = this.$route.params.versionId;
      } else if (this.contentNode.properties) {
        version = this.contentNode.properties["cm:versionLabel"];
      }
      const regex = /\.[^.]+$/;
      const fileNameWithoutExtension = fileName.replace(regex, "");
      link.download = `${fileNameWithoutExtension}-v-${version}${
        fileName.match(regex)[0]
      }`;
      link.click();
    },
    loadImage() {
      this.contentUrl = "";

      if (this.contentNode.id === undefined) {
        return;
      }
      if (this.versionId !== "") {
        this.contentUrl = alfrescoFileService.getVersionContentUrl(
          this.contentNode.id,
          this.versionId,
        );
      } else {
        this.contentUrl = alfrescoFileService.getContentUrl(
          this.contentNode.id,
        );
      }
    },
    fitAuto() {
      this.zoomLevel = 1;
    },
    zoomIn() {
      if (this.zoomLevel < this.maxZoomLevel) {
        this.zoomLevel += 0.4;
      }
    },
    zoomOut() {
      if (this.zoomLevel > this.minZoomLevel) {
        this.zoomLevel -= 0.4;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.viewer-container {
  display: flex;
}

.viewer-content {
  flex-grow: 1;
  overflow: hidden;
}

.consultation-title {
  margin: 0;
}

.toolbar-actions {
  display: flex;
  gap: 0.5rem;
}

.toolbar-btn {
  font-size: 1.25rem;
  padding: 0.5rem;
}

.image-container {
  display: flex;
  justify-content: center;
  margin-right: 0.25rem;
  border: 1px solid var(--p-surface-200);
  border-radius: var(--p-border-radius);
  background-color: var(--p-surface-0);
  padding: 1rem;
}

.p-image-toolbar {
  z-index: 1200;
  background-color: #0b09095e;
  border-radius: 18px;
  margin: 1rem;
  padding: 0 1rem;
}
</style>
